import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import LiveTicker from "../../components/LiveTicker";
import AudienceEngine from "../../components/marketing/AudienceEngine";
import BentoPractices from "../../components/marketing/BentoPractices";
import CinematicHero from "../../components/marketing/CinematicHero";
import ContactForm from "../../components/marketing/ContactForm";
import CredibilityStrip from "../../components/marketing/CredibilityStrip";
import MaterialFlow from "../../components/marketing/MaterialFlow";
import OperationsBridge from "../../components/marketing/OperationsBridge";
import Reveal from "../../components/marketing/Reveal";
import TechPreview from "../../components/marketing/TechPreview";
import { useSeo } from "../../components/marketing/Seo";
import { APPROACH, ENGAGEMENTS, FAQS, SAMPLE_BRIEFINGS, TECH_PILLARS } from "../../data/marketing";

const API = process.env.REACT_APP_BACKEND_URL;

function SectionHeading({ eyebrow, title, lead, dark = false }) {
  return (
    <div className="max-w-3xl">
      <div className={`font-mono text-[11px] font-semibold uppercase tracking-[0.22em] ${dark ? "text-emerald-300" : "text-forest-primary"}`}>{eyebrow}</div>
      <h2 className={`mt-3 font-display text-3xl font-medium tracking-tight sm:text-4xl ${dark ? "text-[#FBF9F4]" : "text-forest-deep"}`}>{title}</h2>
      {lead && <p className={`mt-4 text-base leading-relaxed sm:text-lg ${dark ? "text-emerald-100/70" : "text-forest-deep/65"}`}>{lead}</p>}
    </div>
  );
}

function TechnologySection() {
  return (
    <section id="technology" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28">
      <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
        <div>
          <SectionHeading eyebrow="03 · Our proprietary client technology" title="Consulting sets the direction. Software keeps it connected." lead="CLYMACO is a consulting + SaaS company. Our technology layer brings the advice, data, workflows and evidence of an engagement into a shared client workspace." />
          <div className="mt-8 space-y-4">
            {TECH_PILLARS.map((pillar, index) => (
              <Reveal key={pillar.num} delay={index * 0.05} className="flex gap-4 rounded-2xl border border-forest-deep/10 bg-white p-4 transition-all hover:-translate-y-1 hover:shadow-lg hover:shadow-forest-deep/10">
                <span className="font-mono text-xs font-semibold text-forest-primary">{pillar.num}</span>
                <div>
                  <h3 className="font-display text-lg font-medium text-forest-deep">{pillar.title}</h3>
                  <p className="mt-1 text-sm text-forest-deep/60">{pillar.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <TechPreview />
      </div>
    </section>
  );
}

function InsightsPreview() {
  const [published, setPublished] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/articles`).then((r) => r.json()).then((d) => setPublished((d.items || []).slice(0, 3))).catch(() => {});
  }, []);
  const items = [...SAMPLE_BRIEFINGS.slice(0, 1), ...published, ...SAMPLE_BRIEFINGS.slice(1)].slice(0, 5);
  return (
    <section id="insights" className="bg-[#F4EFE6] py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading eyebrow="06 · Insights and field notes" title="Ideas that help you make the next decision." lead="Perspectives on regulation, waste systems and circularity. From the obligation on paper to the work it requires." />
          <Link to="/insights" className="inline-flex items-center gap-2 rounded-full border border-forest-deep/15 px-5 py-2.5 text-sm font-bold text-forest-deep transition-all hover:-translate-y-0.5 hover:bg-white" data-testid="home-insights-link">
            Visit the insights library <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-5">
          {items.map((article, index) => (
            <Reveal key={article.slug} delay={index * 0.04}>
              <Link to={`/insights/${article.slug}`} className="group flex h-full flex-col rounded-3xl border border-forest-deep/10 bg-white p-5 transition-all hover:-translate-y-1.5 hover:shadow-xl hover:shadow-forest-deep/10" data-testid={`insight-card-${article.slug}`}>
                <span className={`w-fit rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${article.draft ? "bg-amber-500/15 text-amber-800" : "bg-emerald-500/10 text-emerald-800"}`}>
                  {article.draft ? "Sample briefing" : "Published article"}
                </span>
                <h3 className="mt-4 flex-1 font-display text-lg font-medium leading-snug text-forest-deep">{article.title}</h3>
                <p className="mt-3 text-xs leading-relaxed text-forest-deep/55">{article.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function NewHomePage() {
  useSeo({ title: "Consulting + SaaS for EPR, Waste & Circular Economy", path: "/" });
  return (
    <main id="main" className="bg-parchment-base" data-testid="home-page">
      <div className="pt-24">
        <CredibilityStrip compact />
        <LiveTicker />
      </div>
      <CinematicHero />
      <BentoPractices />
      <AudienceEngine />
      <TechnologySection />
      <MaterialFlow />
      <OperationsBridge />

      <section id="approach" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28">
        <SectionHeading eyebrow="04 · Our approach" title="Good advice should lead to a workable system." lead="Clear responsibilities, practical delivery plans and evidence of progress. The same discipline across every CLYMACO mandate." />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {APPROACH.map((step, index) => (
            <Reveal key={step.name} delay={index * 0.05} className="rounded-3xl border border-forest-deep/10 bg-white p-6 transition-all hover:-translate-y-1.5 hover:shadow-xl hover:shadow-forest-deep/10">
              <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-forest-primary">{step.phase}</div>
              <h3 className="mt-4 font-display text-2xl font-medium text-forest-deep">{step.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-forest-deep/65">{step.desc}</p>
              <p className="mt-5 border-t border-forest-deep/10 pt-4 text-xs font-bold text-forest-deep/55">Output: {step.output}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#F4EFE6] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHeading eyebrow="05 · Working with CLYMACO" title="Start with the question. Build the right mandate." lead="Engage us for a defined review, ongoing advisory or the design and oversight of a specific programme." />
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {ENGAGEMENTS.map((mode, index) => (
              <Reveal key={mode.title} delay={index * 0.05} className="rounded-3xl bg-white p-7 shadow-sm transition-all hover:-translate-y-1.5 hover:shadow-xl hover:shadow-forest-deep/10">
                <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-forest-primary">{mode.tag}</div>
                <h3 className="mt-4 font-display text-2xl font-medium text-forest-deep">{mode.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-forest-deep/65">{mode.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <InsightsPreview />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28">
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHeading eyebrow="Before we begin" title="A few useful distinctions." />
          <div className="space-y-3">
            {FAQS.map(([question, answer], index) => (
              <details key={question} className="rounded-2xl border border-forest-deep/10 bg-white p-5 transition-colors open:border-forest-primary/30" data-testid={`faq-accordion-item-${index}`}>
                <summary className="cursor-pointer font-display text-lg font-medium text-forest-deep">{question}</summary>
                <p className="mt-3 text-sm leading-relaxed text-forest-deep/65">{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="bg-forest-deep py-20 text-[#FBF9F4] lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-300">Let's talk</div>
            <h2 className="mt-3 font-display text-3xl font-medium sm:text-5xl">What needs to work better?</h2>
            <p className="mt-5 text-lg leading-relaxed text-emerald-100/70">Your compliance, your site's waste system, a municipal programme or a CSR project. Tell us the challenge and we will discuss an appropriate scope.</p>
            <div className="mt-8 space-y-2 text-sm font-semibold">
              <a href="mailto:alik@clymaco.com" className="block text-emerald-100/80 hover:text-emerald-300">alik@clymaco.com</a>
              <a href="tel:+918240968144" className="block text-emerald-100/80 hover:text-emerald-300">+91 8240968144</a>
            </div>
          </div>
          <ContactForm compact />
        </div>
      </section>
    </main>
  );
}
